import {
  OfferGroupDefinitionDto,
  PerPersonUnitDto,
  ProcessOfferDto,
  ProcessOfferGroupItemDto,
  ProcessOfferSubgroupDishItemDto,
  ProcessOfferSubgroupItemDto,
} from '@/api/generated';
import { gramsPerPortion } from '@/views/processes/offer/util/dishUnitMismatch.util.ts';

export type SetPricing = {
  computedPerPerson: number;
  countsDishes: boolean;
  declaredPerPerson?: number;
  difference: number;
  underpriced: boolean;
};

type UnlimitedAware = { unlimited?: boolean };

const countsDishesIn = (subgroups: UnlimitedAware[]): boolean =>
  subgroups.length === 0 || subgroups.some(subgroup => subgroup.unlimited !== true);

const maxPricePerGram = (dishes: ProcessOfferSubgroupDishItemDto[]): number | null => {
  const rates = dishes
    .map(dish => {
      const grams = gramsPerPortion(dish.measurement);
      return grams == null ? null : dish.price / grams;
    })
    .filter((rate): rate is number => rate != null);
  return rates.length > 0 ? Math.max(...rates) : null;
};

export const subgroupCostPerPerson = (
  subgroup: Pick<
    ProcessOfferSubgroupItemDto,
    'dishes' | 'maxDistinctDishes' | 'maxPerPerson' | 'perPersonUnit'
  > &
    UnlimitedAware,
): number => {
  const dishes = subgroup.dishes ?? [];
  if (subgroup.unlimited === true || dishes.length === 0) {
    return 0;
  }
  const prices = dishes.map(dish => dish.price);

  if (subgroup.maxPerPerson != null) {
    if (subgroup.perPersonUnit !== PerPersonUnitDto.Gram) {
      return subgroup.maxPerPerson * Math.max(...prices);
    }
    const perGram = maxPricePerGram(dishes);
    if (perGram != null) {
      return subgroup.maxPerPerson * perGram;
    }
  }

  const cap = subgroup.maxDistinctDishes;
  const byPriceDesc = [...prices].sort((a, b) => b - a);
  return byPriceDesc.slice(0, cap ?? byPriceDesc.length).reduce((acc, price) => acc + price, 0);
};

export type SubgroupCostBreakdown = {
  columns: { grams: boolean; portions: boolean };
  foodCostTotal: number | null;
  formula: string;
  headline: string;
  marginPercent: number | null;
  note?: string;
  rows: SubgroupCostRow[];
  total: number;
};

export type SubgroupCostRow = {
  counted: boolean;
  foodCost: number | null;
  grams: number | null;
  name: string;
  portionsPerPerson: number | null;
  price: number;
  pricePerGram: number | null;
};

const money = (value: number): string => `${value.toFixed(2)} zł`;

const marginOf = (price: number, foodCost: number | null): number | null =>
  foodCost == null || price <= 0 ? null : ((price - foodCost) / price) * 100;

export const subgroupCostBreakdown = (
  subgroup: Pick<
    ProcessOfferSubgroupItemDto,
    'dishes' | 'maxDistinctDishes' | 'maxPerPerson' | 'perPersonUnit'
  > &
    UnlimitedAware,
): SubgroupCostBreakdown => {
  const dishes = subgroup.dishes ?? [];
  if (subgroup.unlimited === true) {
    return {
      columns: { grams: false, portions: false },
      foodCostTotal: null,
      formula: '',
      headline:
        'Podgrupa bez limitu: wszystko jest podawane bez ograniczeń i mieści się w cenie zestawu, ' +
        'więc nie liczymy porcji ani wartości dań.',
      marginPercent: null,
      rows: [],
      total: 0,
    };
  }
  if (dishes.length === 0) {
    return {
      columns: { grams: false, portions: false },
      foodCostTotal: null,
      formula: '',
      headline: 'Podgrupa nie ma jeszcze dań, więc nie ma z czego liczyć ceny.',
      marginPercent: null,
      rows: [],
      total: 0,
    };
  }
  const byGrams = subgroup.perPersonUnit === PerPersonUnitDto.Gram;
  const budget = subgroup.maxPerPerson;
  const toRow = (dish: ProcessOfferSubgroupDishItemDto): SubgroupCostRow => {
    const grams = gramsPerPortion(dish.measurement);
    return {
      counted: false,
      foodCost: dish.portionFoodCost ?? null,
      grams,
      name: dish.name,
      portionsPerPerson: grams == null || budget == null || !byGrams ? null : budget / grams,
      price: dish.price,
      pricePerGram: grams == null ? null : dish.price / grams,
    };
  };

  if (budget != null && byGrams && maxPricePerGram(dishes) != null) {
    const rows = dishes.map(toRow).sort((a, b) => (b.pricePerGram ?? -1) - (a.pricePerGram ?? -1));
    const winner = rows[0];
    winner.counted = true;
    const perGram = winner.pricePerGram ?? 0;
    const withoutGrams = rows.filter(row => row.grams == null).length;

    return {
      columns: { grams: true, portions: true },
      foodCostTotal:
        winner.foodCost == null || winner.grams == null
          ? null
          : (budget / winner.grams) * winner.foodCost,
      formula: `${budget} g × ${perGram.toFixed(4)} zł/g = ${money(budget * perGram)} na osobę`,
      headline:
        `Gość zjada ${budget} g. Najdrożej wychodzi, gdy weźmie danie o najwyższej cenie za gram — ` +
        'porcje wypadają wtedy ułamkowe, bo liczy się gramatura, a nie sztuki.',
      marginPercent: marginOf(
        budget * perGram,
        winner.foodCost == null || winner.grams == null
          ? null
          : (budget / winner.grams) * winner.foodCost,
      ),
      note:
        withoutGrams > 0
          ? `${withoutGrams} dań na sztuki nie ma gramatury i zostaje poza tym rozliczeniem.`
          : undefined,
      rows,
      total: budget * perGram,
    };
  }

  if (budget != null && !byGrams) {
    const rows = dishes.map(toRow).sort((a, b) => b.price - a.price);
    rows[0].counted = true;

    return {
      columns: { grams: false, portions: false },
      foodCostTotal: rows[0].foodCost == null ? null : budget * rows[0].foodCost,
      formula: `${budget} × ${money(rows[0].price)} = ${money(budget * rows[0].price)} na osobę`,
      headline:
        `Gość bierze do ${budget} porcji i może powtarzać to samo danie, więc najgorszy przypadek ` +
        'to same najdroższe pozycje.',
      marginPercent: marginOf(
        budget * rows[0].price,
        rows[0].foodCost == null ? null : budget * rows[0].foodCost,
      ),
      rows,
      total: budget * rows[0].price,
    };
  }

  const limit = subgroup.maxDistinctDishes;
  const rows = dishes.map(toRow).sort((a, b) => b.price - a.price);
  rows.slice(0, limit ?? rows.length).forEach(row => {
    row.counted = true;
  });
  const counted = rows.filter(row => row.counted);
  const total = counted.reduce((acc, row) => acc + row.price, 0);
  const countedFoodCost = counted.every(row => row.foodCost != null)
    ? counted.reduce((acc, row) => acc + (row.foodCost ?? 0), 0)
    : null;

  return {
    columns: { grams: false, portions: false },
    foodCostTotal: countedFoodCost,
    formula: `${counted.map(row => row.price.toFixed(2)).join(' + ')} = ${money(total)} na osobę`,
    headline:
      limit != null
        ? `Gość wybiera ${limit} różnych dań po jednej porcji i nie powtórzy żadnego, więc liczymy ` +
          `${counted.length} najdroższych z ${rows.length}.`
        : 'Podgrupa nie ma limitów, więc wszystkie dania wchodzą do oferty.',
    marginPercent: marginOf(total, countedFoodCost),
    note:
      budget != null && byGrams
        ? 'Żadne danie nie ma gramatury (same sztuki), więc budżetu gramowego nie ma jak przeliczyć.'
        : undefined,
    rows,
    total,
  };
};

export const computeDefinitionPricePerPerson = (definition: OfferGroupDefinitionDto): number =>
  (definition.groups ?? []).reduce(
    (groupAcc, group) =>
      groupAcc +
      (group.subgroups ?? []).reduce(
        (subAcc, subgroup) => subAcc + subgroupCostPerPerson(subgroup),
        0,
      ),
    0,
  );

export const definitionPricing = (definition: OfferGroupDefinitionDto): SetPricing => {
  const computedPerPerson = computeDefinitionPricePerPerson(definition);
  const declaredPerPerson = definition.pricePerPerson;
  const difference = computedPerPerson - (declaredPerPerson ?? 0);
  return {
    computedPerPerson,
    countsDishes: countsDishesIn((definition.groups ?? []).flatMap(group => group.subgroups ?? [])),
    declaredPerPerson,
    difference,
    underpriced: declaredPerPerson != null && difference > 0.001,
  };
};

export type InsertedSetPricing = SetPricing & {
  definitionName: string;
  groupNames: string[];
  processOfferGroupIds: number[];
  sourceDefinitionId: number;
};

const insertedGroupCostPerPerson = (group: ProcessOfferGroupItemDto): number =>
  (group.subgroups ?? []).reduce((subAcc, subgroup) => subAcc + subgroupCostPerPerson(subgroup), 0);

export const insertedSetsPricing = (offer: ProcessOfferDto): InsertedSetPricing[] => {
  const bySource = new Map<number, ProcessOfferGroupItemDto[]>();
  (offer.groups ?? [])
    .filter(group => group.sourceDefinitionId != null)
    .forEach(group => {
      const key = group.sourceDefinitionId as number;
      bySource.set(key, [...(bySource.get(key) ?? []), group]);
    });

  return [...bySource.entries()].map(([sourceDefinitionId, groups]) => {
    const computedPerPerson = groups.reduce(
      (acc, group) => acc + insertedGroupCostPerPerson(group),
      0,
    );
    const declaredPerPerson = groups[0].sourceDefinitionPricePerPerson;
    const difference = computedPerPerson - (declaredPerPerson ?? 0);
    return {
      computedPerPerson,
      countsDishes: countsDishesIn(groups.flatMap(group => group.subgroups ?? [])),
      declaredPerPerson,
      definitionName: groups[0].sourceDefinitionName ?? '',
      difference,
      groupNames: groups.map(group => group.name),
      processOfferGroupIds: groups.map(group => group.processOfferGroupId),
      sourceDefinitionId,
      underpriced: declaredPerPerson != null && difference > 0.001,
    };
  });
};
